import { useCallback, useContext, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import Button from '../../components/Button';
import EmailField from '../../components/EmailField';
import PageContainer from '../../components/PageContainer';
import PasswordField from '../../components/PasswordField';
import PasswordRequirementsValidator from '../../components/PasswordRequirementsValidator';
import InsecurePasswordMessage from '../../components/InsecurePasswordMessage';
import { getFirstError, getErrorMessage, showErrorNotification } from '../../services/errorUtils';
import ResponseStatus from '../../constants/ResponseStatus';
import ResponseErrorTypes from '../../constants/ResponseErrorTypes';
import useMessageService from '../../services/i18n/useMessageService';
import useNotificationCookie from '../../services/useNotificationCookie';
import useRequestState from '../../services/useRequestState';
import { updatePassword } from '../../veevaIdApi';
import PasswordSavedConfirmation from './PasswordSavedConfirmation';
import useNetworkErrorNotification from '../../services/useNetworkErrorNotification';
import useChangePassword from '../../services/useChangePassword';
import LoadingScreenContext from '../../services/LoadingScreenContext';
import UnauthenticatedRouteLanguageContext from '../../services/UnauthenticatedRouteLanguageContext';
import getTargetAttribute from '../../services/getTargetAttribute';
import { UnauthenticatedPaths } from '../../constants/Paths';

const ChangePasswordPage = () => {
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const history = useHistory();
    const { showNotification } = useNetworkErrorNotification();

    const { setLoading } = useContext(LoadingScreenContext);
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [passwordResetTargetUrl, setPasswordResetTargetUrl] = useState();
    const [allPasswordRequirementsMet, setAllPasswordRequirementsMet] = useState(false);
    const requestState = useRequestState();
    const { newPassword, setNewPassword, confirmedPassword, setConfirmedPassword } =
        useChangePassword();
    const notificationCookie = useNotificationCookie() ?? {};
    const { email, language, locale, timezone } = notificationCookie;

    const [
        [
            newPasswordSavedLabel,
            resetYourPasswordLabel,
            saveYourPasswordLabel,
            passwordPlaceholderLabel,
            confirmPasswordPlaceholderLabel,
        ],
    ] = useMessageService([
        'change_password.password_saved_title',
        'password.reset_password',
        'password.save_password',
        'general.password',
        'password.confirm_password_placeholder',
    ]);

    useEffect(() => {
        setNewPasswordError('');
        setConfirmPasswordError('');
    }, [messageLanguage]);

    const handleNewPasswordChange = (value) => {
        setNewPassword(value);
    };

    const handleErrorResponse = async (response) => {
        const error = getFirstError(response);
        const errorMessage = await getErrorMessage(error, messageLanguage);

        switch (error.type) {
            case ResponseErrorTypes.VEEVAID_INVALID_NEW_PASSWORD: {
                setNewPasswordError(errorMessage);
                break;
            }
            case ResponseErrorTypes.VEEVAID_INSECURE_PASSWORD: {
                setConfirmPasswordError(<InsecurePasswordMessage />);
                break;
            }
            default: {
                const i18nOptions = { language, locale, timezone };
                await showErrorNotification(response.errors, i18nOptions);
            }
        }
    };

    const handleSubmitPassword = async () => {
        setLoading(true);
        const response = await updatePassword(confirmedPassword, requestState);
        setLoading(false);
        if (allPasswordRequirementsMet) {
            switch (response.responseStatus) {
                case ResponseStatus.SUCCESS:
                    setPasswordResetTargetUrl(response.data?.targetUrl);
                    setNewPasswordError('');
                    setConfirmPasswordError('');
                    break;
                case ResponseStatus.FAILURE:
                    await handleErrorResponse(response);
                    break;
                case ResponseStatus.UI_NETWORK_ERROR:
                    await showNotification();
                    break;
                default:
                // No-op
            }
        }
    };

    const handlePasswordConfirmChange = useCallback(({ valid, inlineError }) => {
        setAllPasswordRequirementsMet(valid && !inlineError);
        setNewPasswordError(inlineError);
        setConfirmPasswordError('');
    }, []);

    const containerTitle = passwordResetTargetUrl ? (
        <span data-target={getTargetAttribute('change-password-confirmation-title')}>
            {newPasswordSavedLabel}
        </span>
    ) : (
        resetYourPasswordLabel
    );

    if (!email && !passwordResetTargetUrl) {
        history.replace(UnauthenticatedPaths.LOGIN);
        return null;
    }

    return (
        <PageContainer title={containerTitle}>
            {passwordResetTargetUrl ? (
                <PasswordSavedConfirmation
                    targetUrl={passwordResetTargetUrl}
                    requestState={requestState}
                />
            ) : (
                <>
                    <EmailField value={email} readOnly />
                    <PasswordField
                        onChange={handleNewPasswordChange}
                        value={newPassword}
                        placeholder={passwordPlaceholderLabel}
                        errorMessage={newPasswordError}
                        error={!!confirmPasswordError}
                    />
                    <PasswordField
                        onChange={setConfirmedPassword}
                        onKeyUp={handleSubmitPassword}
                        placeholder={confirmPasswordPlaceholderLabel}
                        value={confirmedPassword}
                        errorMessage={confirmPasswordError}
                    />
                    <PasswordRequirementsValidator
                        password={newPassword}
                        passwordConfirmation={confirmedPassword}
                        onStateChange={handlePasswordConfirmChange}
                    />
                    <Button
                        css={css`
                            margin-top: 3.333rem;
                        `}
                        type="submit"
                        onClick={handleSubmitPassword}
                        disabled={!allPasswordRequirementsMet}
                        data-target={getTargetAttribute('submit-button')}
                    >
                        {saveYourPasswordLabel}
                    </Button>
                </>
            )}
        </PageContainer>
    );
};

export default ChangePasswordPage;
